import React from 'react';
import { Clock, Code, Hash, Tag } from 'lucide-react';
import type { DetrixEvent, DetrixSession } from '../../types';
import { ErrorNarrative } from '../../components/ErrorNarrative';
import { formatSessionOffset } from '../../utils/time';
import { formatUnknownJsonForDisplay } from '../../utils/prettifyJson';

interface EventInspectorProps {
  session: DetrixSession;
  event: DetrixEvent;
}

function isEventError(ev: DetrixEvent): boolean {
  return ev.type === 'error' || ev.type === 'system.error';
}

export const EventInspector: React.FC<EventInspectorProps> = ({ session, event }) => {
  const err = isEventError(event);
  const payload = event.payload as Record<string, unknown> | null | undefined;
  const headline =
    (payload && typeof payload.message === 'string' && payload.message) ||
    (payload && typeof payload.error === 'string' && payload.error) ||
    '';

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[var(--bg-panel)]">
      <ErrorNarrative session={session} />
      {err ? (
        <div className="shrink-0 border-b border-[var(--inspector-error-border)] bg-[var(--inspector-error-bg)] px-3 py-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--inspector-error-title)]">Error</div>
          {headline ? <p className="mt-1 text-[12px] text-[var(--inspector-error-body)]">{headline}</p> : null}
        </div>
      ) : null}
      <div className="border-b p-3">
        <div className="mb-1 flex items-center gap-2">
          <Tag className={`h-4 w-4 ${err ? 'text-[var(--error)]' : 'text-[var(--ok)]'}`} />
          <h3 className={`text-[14px] font-semibold ${err ? 'text-[var(--error)]' : 'text-[var(--text-primary)]'}`}>
            {event.type}
          </h3>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[var(--text-muted)]">
          <span className="flex items-center gap-1">
            <Hash className="h-3 w-3" /> {event.sequence}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> {formatSessionOffset(event.timestamp)}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overflow-x-hidden p-3">
        {event.selector ? (
          <section>
            <h4 className="mb-2 text-[11px] font-bold uppercase text-[var(--text-muted)]">Selector</h4>
            <div className="break-all rounded border bg-[var(--bg-editor)] p-2 font-mono text-[11px] text-[var(--accent)]">
              {event.selector}
            </div>
          </section>
        ) : null}

        <section>
          <h4 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase text-[var(--text-muted)]">
            <Code className="h-3 w-3" /> Payload
          </h4>
          <div className="overflow-hidden rounded border border-[var(--border)] bg-[var(--bg-editor)] p-2 font-mono text-[11px] text-[var(--code-json-fg)]">
            <pre className="whitespace-pre-wrap break-words">{formatUnknownJsonForDisplay(event.payload)}</pre>
          </div>
        </section>
      </div>
    </div>
  );
};
